import { v4 as uuidv4 } from "uuid";
import { QuestionType } from "api/landlord-surveys/types/questions";
import { DirtyField, QuestionPayload } from "api/landlord-surveys/types/types";
import { create } from "zustand";
import {
  DraftDetailedLandlordSurvey,
  DraftSurveyTemplateQuestion
} from "./types";

type Store = {
  data: DraftDetailedLandlordSurvey | null;
  apiData: DraftDetailedLandlordSurvey | null;
  isDirty: boolean;
  dirtyFields: Set<DirtyField>;

  activeQuestionIdx: number | null;

  resetData: () => void;
  resetApiData: (data: DraftDetailedLandlordSurvey | null) => void;
  updateData: (data: DraftDetailedLandlordSurvey, dirty?: DirtyField[]) => void;
  updateActiveQuestionIdx: (activeQuestionIdx: number | null) => void;

  updateQuestionOrderByIdx: (prevIdx: number, newIdx: number) => void;

  updateActiveQuestion: (
    question:
      | DraftSurveyTemplateQuestion
      | ((prev: DraftSurveyTemplateQuestion) => DraftSurveyTemplateQuestion)
  ) => void;

  duplicateQuestion: () => void;
  addNewQuestion: (index?: number) => void;
  deleteQuestion: () => void;
};

const markDirty = (
  state: Store,
  ...fields: DirtyField[]
): { isDirty: true; dirtyFields: Set<DirtyField> } => {
  const next = new Set(state.dirtyFields);
  for (const f of fields) next.add(f);
  return { isDirty: true, dirtyFields: next };
};

export const selectActiveQuestion = (
  state: Store
): DraftSurveyTemplateQuestion | undefined =>
  state.data?.questions.find((q) => q.index === state.activeQuestionIdx);

export const selectSortedQuestions = (
  state: Store
): DraftSurveyTemplateQuestion[] =>
  state.data?.questions.slice().sort((a, b) => a.index - b.index) ?? [];

export const selectCanPreview = (state: Store): boolean => {
  const questions = state.data?.questions ?? [];
  return (
    questions.length > 0 && questions.every((q) => q.question.trim() !== "")
  );
};

export const toQuestionPayload = (
  q: DraftSurveyTemplateQuestion
): QuestionPayload => ({
  internal_id: q.internal_id,
  index: q.index,
  question: q.question,
  description: q.description,
  type: q.type,
  is_required: q.is_required,
  ...("options" in q && q.options ? { options: { values: q.options } } : {})
});

export const useSurveyStore = create<Store>((set) => ({
  data: null,
  apiData: null,
  isDirty: false,
  dirtyFields: new Set(),

  activeQuestionIdx: 0,

  updateQuestionOrderByIdx: (prevIdx: number, newIdx: number) => {
    set((state) => {
      if (!state.data) return state;

      const questions = state.data.questions
        .slice()
        .sort((a, b) => a.index - b.index);

      const [moved] = questions.splice(prevIdx, 1);
      questions.splice(newIdx, 0, moved);

      const reorderedQuestions = questions.map((q, i) => ({
        ...q,
        index: i
      }));

      return {
        ...state,
        data: {
          ...state.data,
          questions: reorderedQuestions
        },
        activeQuestionIdx: newIdx,
        ...markDirty(state, "questions")
      };
    });
  },
  resetData: () => {
    set((state) => ({
      ...state,
      data: state.apiData,
      activeQuestionIdx: 0,
      isDirty: false,
      dirtyFields: new Set()
    }));
  },
  resetApiData: (data) => {
    set(() => ({
      data,
      apiData: data,
      isDirty: false,
      dirtyFields: new Set(),
      activeQuestionIdx: 0
    }));
  },
  updateActiveQuestionIdx: (activeQuestionIdx: number | null) => {
    set((state) => ({ ...state, activeQuestionIdx }));
  },
  updateData: (
    data:
      | DraftDetailedLandlordSurvey
      | ((
          prev: DraftDetailedLandlordSurvey | null
        ) => DraftDetailedLandlordSurvey | null),
    dirty?: DirtyField[]
  ) => {
    set((state) => {
      const nextData = typeof data === "function" ? data(state.data) : data;
      return {
        ...state,
        data: nextData,
        ...markDirty(state, ...(dirty ?? []))
      };
    });
  },
  updateActiveQuestion: (
    question:
      | DraftSurveyTemplateQuestion
      | ((prev: DraftSurveyTemplateQuestion) => DraftSurveyTemplateQuestion)
  ) => {
    set((state) => {
      if (!state.data) return state;

      const prev = state.data.questions.find(
        (q) => q.index === state.activeQuestionIdx
      );
      if (!prev) return state;

      const updated =
        typeof question === "function" ? question(prev) : question;

      return {
        ...state,
        ...markDirty(state, "questions"),
        data: {
          ...state.data,
          questions: state.data.questions.map((q) =>
            q.index === updated.index ? updated : q
          )
        }
      };
    });
  },

  duplicateQuestion: () => {
    set((state) => {
      if (!state.data || state.activeQuestionIdx === null) return state;

      const source = state.data.questions.find(
        (q) => q.index === state.activeQuestionIdx
      );
      if (!source) return state;

      const newIndex = state.data.questions.length;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars -- strip server id from duplicate
      const { id, ...rest } = source;
      const duplicate = {
        ...rest,
        internal_id: uuidv4(),
        index: newIndex
      };

      return {
        ...state,
        ...markDirty(state, "questions"),
        activeQuestionIdx: newIndex,
        data: {
          ...state.data,
          questions: [...state.data.questions, duplicate]
        }
      };
    });
  },

  addNewQuestion: (index?: number) => {
    set((state) => {
      if (!state.data) return state;

      const questions = state.data.questions
        .slice()
        .sort((a, b) => a.index - b.index);

      const insertAt = index ?? questions.length;

      const newQuestion: DraftSurveyTemplateQuestion = {
        internal_id: uuidv4(),
        index: insertAt,
        question: "",
        description: "",
        is_required: false,
        type: QuestionType.LONG_TEXT
      };

      questions.splice(insertAt, 0, newQuestion);
      const reindexed = questions.map((q, i) => ({ ...q, index: i }));

      return {
        ...state,
        ...markDirty(state, "questions"),
        activeQuestionIdx: insertAt,
        data: {
          ...state.data,
          questions: reindexed
        }
      };
    });
  },

  deleteQuestion: () => {
    set((state) => {
      if (!state.data || state.activeQuestionIdx === null) return state;

      const sorted = state.data.questions
        .slice()
        .sort((a, b) => a.index - b.index);

      const deleteIdx = state.activeQuestionIdx;
      const filtered = sorted.filter((q) => q.index !== deleteIdx);

      if (filtered.length === 0)
        return {
          ...state,
          ...markDirty(state, "questions"),
          activeQuestionIdx: null,
          data: { ...state.data, questions: [] }
        };

      const reindexed = filtered.map((q, i) => ({ ...q, index: i }));

      const newActiveIdx = deleteIdx > 0 ? deleteIdx - 1 : 0;

      return {
        ...state,
        ...markDirty(state, "questions"),
        activeQuestionIdx: newActiveIdx,
        data: {
          ...state.data,
          questions: reindexed
        }
      };
    });
  }
}));
